import * as React from 'react'

import { CheckIcon } from 'lucide-react'
import { action, makeObservable } from 'mobx'
import { classList } from 'react-classlist-helper'

import { DeliveryStatus } from '~/client/graph'

import Delivery from '../../models/Delivery'
import ActionBarBottomMenu from '../ActionBarBottomMenu/ActionBarBottomMenu'
import DeliveryDetailsStore from '../DeliveryDetails/DeliveryDetails.store'
import DeliveryStatusLabel from '../DeliveryStatusLabel/DeliveryStatusLabel'

import './DeliveryStatusSelector.scss'

// translated

interface IProps {
  onClose: () => void
  deliveryDetailsStore: DeliveryDetailsStore
  displayedDelivery: Delivery
  isMenuPopupShown: boolean
  selectedStatus: DeliveryStatus

  setSelectedStatus: (status: DeliveryStatus) => void
}

const hiddenStatuses = [
  DeliveryStatus.Deleted,
  DeliveryStatus.Delivering,
  DeliveryStatus.Paused,
  DeliveryStatus.OnHold,
]

export default class DeliveryStatusSelector extends React.Component<IProps> {
  public constructor(props: IProps) {
    super(props)
    makeObservable(this)
  }

  public componentDidUpdate(prevProps: IProps) {
    const { displayedDelivery, setSelectedStatus } = this.props
    if (
      prevProps.displayedDelivery &&
      prevProps.displayedDelivery.id !== displayedDelivery.id
    ) {
      setSelectedStatus(displayedDelivery.status)
    }
  }

  public componentDidMount() {
    const { displayedDelivery, setSelectedStatus } = this.props
    if (displayedDelivery) {
      setSelectedStatus(displayedDelivery.status)
    }
  }

  public render() {
    const { isMenuPopupShown } = this.props

    return (
      <>
        <ActionBarBottomMenu isOpen={isMenuPopupShown}>
          <div className="delivery-status-selector">
            <div className="col bt-palette-brand-lighter full-height scrollable">
              {Object.values(DeliveryStatus)
                .filter(ds => !hiddenStatuses.includes(ds))
                .map(deliveryStatus => this.renderStatus(deliveryStatus))}
            </div>
          </div>
        </ActionBarBottomMenu>
      </>
    )
  }

  private renderStatus = (status: DeliveryStatus): JSX.Element => {
    return (
      <div
        className={classList({
          'row x-between bb-palette-brand-lighter pa10': true,
          'inactive-element': this.isDisabled(status),
        })}
        onClick={this.selectStatus.bind(this, status)}
        key={status}
      >
        <DeliveryStatusLabel status={status} className="no-grow" />
        {this.props.selectedStatus === status && (
          <CheckIcon size={16} className="tw:text-primary" />
        )}
      </div>
    )
  }

  @action.bound
  private selectStatus(status: DeliveryStatus) {
    const { deliveryDetailsStore, setSelectedStatus, onClose } = this.props
    const { toggleCancelConfirmModal, onButtonClick, changeDelivery } =
      deliveryDetailsStore

    switch (status) {
      case DeliveryStatus.Canceled:
        toggleCancelConfirmModal()
        break
      // Changed submits the edited fields, so it goes through the form path,
      // not a status-only copy — and a recurring delivery must be asked whether
      // the change applies to the series.
      case DeliveryStatus.Changed:
        onButtonClick(
          changeDelivery,
          deliveryDetailsStore.getActionFromStatus(status).actionTitle,
        )
        break
      default:
        setSelectedStatus(status)
    }
    onClose()
  }

  private isDisabled = (status: DeliveryStatus) => {
    const {
      isDenyButtonEnabled,
      isSubmitButtonEnabled,
      canCancelDelivery,
      getActionFromStatus,
      isAnyDialogOpened,
    } = this.props.deliveryDetailsStore

    switch (status) {
      case DeliveryStatus.Denied:
        return !isDenyButtonEnabled
      case DeliveryStatus.Changed:
        // Submits the form, so it must also wait on any open dialog — the other
        // statuses get that from getActionFromStatus.
        return !isSubmitButtonEnabled || isAnyDialogOpened
      case DeliveryStatus.Canceled:
        return !canCancelDelivery
      default:
        return (
          getActionFromStatus(status).isDisabled ||
          isAnyDialogOpened ||
          isSubmitButtonEnabled
        )
    }
  }
}
